import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import { Contract, AmendmentPayload, NetTerms, NET_TERMS_OPTIONS } from "./contractTypes";
import { renderMarkdown } from "./markdownUtils";

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

interface Props {
  contract: Contract;
  onClose: () => void;
  onSaved: () => void;
}

export default function AmendmentEditor({ contract, onClose, onSaved }: Props) {
  const api = useApi();
  const [saving, setSaving] = useState(false);
  const [signingLink, setSigningLink] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [title, setTitle] = useState(`Amendment #${(contract.amendments?.length ?? 0) + 1} — `);
  const [body, setBody] = useState(
    `This Amendment modifies the Master Services Agreement dated ${contract.serviceStartDate} between the parties.\n\n## Changes\n\n- [Describe what is being added or changed]\n\n## Effective Date\n\nAll changes take effect on the date signed below.`
  );
  const [newMonthlyValue, setNewMonthlyValue] = useState(contract.totalMonthlyValue);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [proratedAmount, setProratedAmount] = useState<number>(0);
  const [netTermsOverride, setNetTermsOverride] = useState<NetTerms | "">(""); // empty = inherit
  const [toast, setToast] = useState<string | null>(null);

  const delta = newMonthlyValue - contract.totalMonthlyValue;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function buildPayload(): Promise<AmendmentPayload> {
    return {
      title,
      body,
      newMonthlyValue,
      proratedAmount: proratedAmount > 0 ? proratedAmount : undefined,
      effectiveDate,
      netTerms: netTermsOverride || undefined,
    };
  }

  async function handleSaveDraft() {
    if (!title.trim() || !body.trim() || !effectiveDate) return;
    setSaving(true);
    try {
      const payload = await buildPayload();
      const res = await api.admin.post(`api/Contract/${contract.id}/amendment`, payload);
      if (res.ok) {
        onSaved();
        onClose();
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndSend() {
    if (!title.trim() || !body.trim() || !effectiveDate) return;
    setSaving(true);
    try {
      const payload = await buildPayload();
      const res = await api.admin.post(`api/Contract/${contract.id}/amendment`, payload);
      if (res.ok) {
        const amend = await res.json();
        const sendRes = await api.admin.post(`api/Contract/${contract.id}/amendment/${amend.id}/send`, {});
        if (sendRes.ok) {
          const origin = window.location.origin;
          const token = amend.signingToken ?? amend.id;
          setSigningLink(`${origin}/sign/${token}`);
          onSaved();
        }
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  function copyLink() {
    if (!signingLink) return;
    navigator.clipboard.writeText(signingLink).then(() => showToast("Signing link copied!"));
  }

  const isValid = title.trim() && body.trim() && effectiveDate && newMonthlyValue >= 0;

  return (
    <>
      {toast && (
        <div className="fixed top-4 right-4 bg-slate-800 text-white text-sm px-4 py-3 rounded-lg shadow-lg z-[60]">
          {toast}
        </div>
      )}

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Add Amendment</h2>
              <p className="text-sm text-slate-500">
                To: <strong>{contract.title}</strong> — {contract.customerName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* Original contract context */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm">
              <p className="font-medium text-slate-700 mb-2">Original Contract Summary</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-slate-500">Current Monthly</div>
                  <div className="font-semibold text-slate-800">{fmtCurrency(contract.totalMonthlyValue)}</div>
                </div>
                <div>
                  <div className="text-slate-500">Start Date</div>
                  <div className="font-medium text-slate-800">{contract.serviceStartDate}</div>
                </div>
                <div>
                  <div className="text-slate-500">Net Terms</div>
                  <div className="font-medium text-slate-800">{contract.netTerms}</div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Amendment Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                placeholder="Amendment #1 — Plan Upgrade to Enterprise"
              />
            </div>

            {/* What changed */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700">
                  What Changed <span className="text-red-400">*</span>
                </label>
                <button
                  onClick={() => setShowPreview((p) => !p)}
                  className="text-xs text-primary hover:underline"
                >
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </button>
              </div>
              <div className={`grid gap-4 ${showPreview ? "grid-cols-2" : "grid-cols-1"}`}>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-hover resize-none"
                  placeholder="Describe the changes being made to the original agreement..."
                />
                {showPreview && (
                  <div
                    className="border border-slate-200 rounded-lg p-4 overflow-y-auto text-sm text-slate-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }}
                  />
                )}
              </div>
            </div>

            {/* Pricing + dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  New Monthly Value <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={newMonthlyValue || ""}
                    onChange={(e) => setNewMonthlyValue(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  />
                </div>
                {/* Delta callout */}
                <p className={`text-xs mt-1 font-medium ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-500" : "text-slate-400"}`}>
                  {fmtCurrency(contract.totalMonthlyValue)} → {fmtCurrency(newMonthlyValue)}
                  {delta !== 0 && ` (${delta > 0 ? "+" : ""}${fmtCurrency(delta)}/mo)`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Effective Date <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Prorated Charge (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={proratedAmount || ""}
                    onChange={(e) => setProratedAmount(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Prorated charge for remainder of billing period</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Net Terms Override (optional)
                </label>
                <select
                  value={netTermsOverride}
                  onChange={(e) => setNetTermsOverride(e.target.value as NetTerms | "")}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                >
                  <option value="">— Inherit from original ({contract.netTerms}) —</option>
                  {NET_TERMS_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Signing link after send */}
            {signingLink && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-green-700 mb-2">Amendment sent for signature!</p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={signingLink}
                    className="flex-1 border border-green-300 rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                  />
                  <button
                    onClick={copyLink}
                    className="px-3 py-2 text-xs font-medium bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            {signingLink ? (
              <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleSaveDraft}
                  disabled={saving || !isValid}
                  className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Amendment Draft"}
                </button>
                <button
                  onClick={handleSaveAndSend}
                  disabled={saving || !isValid}
                  className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary disabled:opacity-50"
                >
                  {saving ? "Sending..." : "Save & Send for Signing"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
